import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useGetStartedMilestones } from "@/components/audiolab/GetStartedTracker/useGetStartedMilestones";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { hasSellerPersona } from "@/lib/personas";
import SpotlightOverlay from "./SpotlightOverlay";
import {
  AUDIOLAB_TOUR_STEPS_DESKTOP,
  AUDIOLAB_TOUR_STEPS_MOBILE,
} from "./tourSteps";
import { useMarkAudiolabTourSeen } from "./useMarkAudiolabTourSeen";

// lg breakpoint — picks the target set: the sidebar coachmarks above it, the
// bottom tab-bar coachmarks below it.
const DESKTOP_QUERY = "(min-width: 1024px)";

// Opening the instant the queries resolve reads as abrupt, and the beat lets
// the layout settle before the first spotlight measures.
const START_DELAY_MS = 2000;

/**
 * Runs the artist Audiolab tour exactly once, ever. Self-gating and globally
 * mounted (next to the persona picker): it starts only for an artist on an
 * `/audiolab` route who hasn't seen it, spotlights the sidebar on desktop or
 * the bottom tab bar on mobile, and records "seen" on the server the moment the
 * tour finishes or is dismissed.
 */
export default function AudiolabTourContainer() {
  const { data: user, isLoading } = useLoggedInUser();
  const location = useLocation();
  const { mutate: markSeen } = useMarkAudiolabTourSeen();

  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DESKTOP_QUERY).matches
      : false,
  );
  const [active, setActive] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const isSeller = hasSellerPersona(user?.profile?.personas);
  const tourSeen = user?.profile?.audiolabTourSeen === true;
  const onAudiolab = location.pathname.startsWith("/audiolab");

  // Mounted globally, so these queries stand in for "the Audiolab page has its
  // data". The tracker requests the same set, so React Query dedupes them.
  const { isLoading: milestonesLoading, allComplete } = useGetStartedMilestones(
    {
      enabled: isSeller && onAudiolab,
    },
  );

  const eligible =
    !isLoading &&
    !!user &&
    isSeller &&
    !tourSeen &&
    onAudiolab &&
    !milestonesLoading;

  // Seen is recorded when step 1 appears, not on completion — abandoning still
  // counts. The ref is set only once the timer fires, so losing eligibility
  // mid-countdown reschedules rather than burning the start.
  useEffect(() => {
    if (!eligible || startedRef.current) return;
    const timeout = setTimeout(() => {
      startedRef.current = true;
      setActive(true);
      markSeen();
    }, START_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [eligible, markSeen]);

  // Leaving Audiolab closes the tour; it does not come back, since the flag was
  // consumed when it opened.
  useEffect(() => {
    if (active && !onAudiolab) setActive(false);
  }, [active, onAudiolab]);

  if (!active) return null;

  const close = () => setActive(false);

  const allSteps = isDesktop
    ? AUDIOLAB_TOUR_STEPS_DESKTOP
    : AUDIOLAB_TOUR_STEPS_MOBILE;
  // The last step targets the tracker, which unmounts once every milestone is
  // done — dropping it beats ending on a blank scrim.
  const steps = allComplete ? allSteps.slice(0, -1) : allSteps;

  return (
    <SpotlightOverlay
      key={isDesktop ? "desktop" : "mobile"}
      steps={steps}
      onFinish={close}
      onSkip={close}
    />
  );
}
